import React from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import { useUser } from './UserContext';
import WelcomeCard from './WelcomeCard';

const Terms = () => {
  const { user } = useUser();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      flex: 1,
      paddingTop: '50px',
      paddingBottom: '50px',
    }}>
      <div style={{position: 'absolute', left: '0px', top: '0px', width:'100%', opacity: '10%', pointerEvents: 'none' }}>
        <img src='/mascot.png' alt=''/>
      </div>

      {!user && (
        <WelcomeCard />
      )}

      <Paper
        elevation={6}
        sx={{
          padding: '40px',
          maxWidth: '800px',
          width: '100%',
          marginBottom: '50px',
          backgroundColor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary,
        }}
      >
        <Typography variant="h3" gutterBottom sx={{ textAlign: 'center', marginBottom: '30px' }}>
          Terms of Use
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Agreement to Terms
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish is a platform owned and operated by Five Pack Creative, Inc., a Texas corporation. By creating an account, logging into Snapperish, or using our services, you agree to be bound by these Terms of Use, our{' '}
          <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>
            Privacy Policy
          </Link>, and our{' '}
          <Link to="/acceptableuse" style={{ color: 'inherit', textDecoration: 'underline' }}>
            Acceptable Use Policy
          </Link>. If you do not agree to these terms, you may not use Snapperish.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Service Description
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish is a platform designed for clubs and organizations to create, manage, and participate in photo contests. Our services include:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Contest creation and management tools for club owners
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Photo submission and voting systems for club members
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Secure storage and display of contest photos
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Administrative tools for contest oversight
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Account Types and Registration
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Club Owners:</strong> May create accounts independently and are responsible for managing their club's contests and member lists.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Club Members:</strong> Join Snapperish by accepting an invitation from a club owner. If you don't yet have an account, you'll be prompted to create one when you accept the invitation.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Account Requirements:</strong> You must be 13 years of age or older to create an account or directly interact with Snapperish. You must provide accurate information and maintain the security of your account credentials.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Age Requirements & Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish is not directed at children and is not intended to be used directly by anyone under the age of 13. Children under 13 may not create accounts, log in, upload photos, or otherwise interact with the platform directly.
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish does support photo contests in which children participate — for example, youth photography clubs. However, in all such cases, a parent or guardian must be the one interacting with the platform on the child's behalf. This includes creating the club account, managing memberships, and uploading photos.
        </Typography>
        <Typography variant="body1" paragraph>
          Club owners who invite members are responsible for ensuring that any invitation sent on behalf of a child under 13 is directed to that child's parent or guardian. The parent or guardian must be the one who creates the account and interacts with the platform on the child's behalf.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          User Responsibilities
        </Typography>
        <Typography variant="body1" paragraph>
          By using Snapperish, you agree to:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Comply with all applicable laws and regulations
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Follow our{' '}
            <Link to="/acceptableuse" style={{ color: 'inherit', textDecoration: 'underline' }}>
              Acceptable Use Policy
            </Link>{' '}
            when uploading content
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Respect the intellectual property rights of others
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Only upload photos you own or have permission to use
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Maintain the confidentiality of your login credentials
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Report violations of these terms or inappropriate content
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Use the platform only for legitimate club contest purposes
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Photo Rights and Licensing
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Ownership:</strong> You retain ownership of all photos you upload to Snapperish.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>License Grant:</strong> By uploading photos, you grant your club and Snapperish a limited license to:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Display photos within contest platforms and voting systems</Typography>
          <Typography component="li" variant="body1">Allow club use for calendars, newsletters, and promotional materials</Typography>
          <Typography component="li" variant="body1">Allow your club to create products such as calendars and other merchandise using your photos</Typography>
          <Typography component="li" variant="body1">Enable contest administration and judging processes</Typography>
          <Typography component="li" variant="body1">Facilitate platform operation and technical support</Typography>
        </Box>
        <Typography variant="body1" paragraph>
          This license does not permit use outside your club without your explicit permission.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Prohibited Activities
        </Typography>
        <Typography variant="body1" paragraph>
          You may not:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Upload content that violates our Acceptable Use Policy
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Attempt to circumvent security measures or access restrictions
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Share account credentials with unauthorized persons
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Use automated systems to interact with the platform
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Interfere with other users' ability to use the service
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Reverse engineer or attempt to extract source code
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Use the platform for commercial purposes outside of club activities
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Platform Availability and Modifications
        </Typography>
        <Typography variant="body1" paragraph>
          Five Pack Creative, Inc. reserves the right to:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Modify or discontinue services with no less than 30 days' prior written notice to active subscribers, delivered via email or platform notification. In cases of discontinuation, notice will be provided at least 30 days before the effective date to allow club owners to export their data.</Typography>
          <Typography component="li" variant="body1">Perform maintenance and updates that may temporarily affect availability</Typography>
          <Typography component="li" variant="body1">Remove content that violates our policies</Typography>
          <Typography component="li" variant="body1">Suspend or terminate accounts for policy violations</Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Payment and Subscriptions
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Subscription Tiers:</strong> Snapperish offers multiple subscription options ranging from Starter to Large Club tiers. For current pricing, member limits, and feature details, please see our{' '}
          <Link to="/pricing" style={{ color: 'inherit', textDecoration: 'underline' }}>
            Pricing page
          </Link>.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Payment Processing:</strong> All payments are processed securely through Stripe. Subscriptions are billed monthly and may be canceled at any time. Snapperish does not receive or store any financial information.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Cancellation:</strong> You may cancel your subscription at any time. Service continues until the end of the current billing period.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Club Owner Responsibility:</strong> Club owners are responsible for maintaining active subscriptions during contest periods and ensuring their chosen tier accommodates their club size.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Limitation of Liability
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish is provided "as is" and "as available" without warranties of any kind, express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement. We are not liable for:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Loss of photos or data due to technical issues</Typography>
          <Typography component="li" variant="body1">Disputes between club members or contest participants</Typography>
          <Typography component="li" variant="body1">Unauthorized use of photos by club members</Typography>
          <Typography component="li" variant="body1">Service interruptions or technical difficulties</Typography>
        </Box>
        <Typography variant="body1" paragraph>
          In no event shall Five Pack Creative, Inc.'s total liability to you exceed the greater of $500.00 or the total amounts paid by you to Snapperish during any consecutive 24-month period preceding the claim.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Dispute Resolution, Arbitration, and Class Action Waiver
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Informal Resolution:</strong> Before initiating any formal dispute, you agree to first contact Five Pack Creative, Inc. at{' '}
          <a href="mailto:matt+snapperish+privacy@fivepack.com" style={{ color: 'inherit' }}>matt+snapperish+privacy@fivepack.com</a>{' '}
          and provide a written description of the dispute, your requested resolution, and your contact information. The parties agree to attempt to resolve the dispute informally for a period of thirty (30) days from the date of notice.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Binding Arbitration:</strong> If the dispute cannot be resolved informally, you and Five Pack Creative, Inc. agree that any dispute, claim, or controversy arising out of or relating to these Terms of Use or the use of Snapperish — including questions of arbitrability — shall be resolved by binding individual arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules, which are available at www.adr.org. The arbitration shall be conducted in Collin County, Texas, or remotely if agreed by both parties. The arbitrator's decision shall be final and binding and may be entered as a judgment in any court of competent jurisdiction.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Exceptions:</strong> Either party may seek emergency injunctive or equitable relief from a court of competent jurisdiction to prevent irreparable harm pending arbitration. Claims that are eligible for small claims court may be brought in small claims court in Collin County, Texas rather than in arbitration.
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
          CLASS ACTION WAIVER: YOU AND FIVE PACK CREATIVE, INC. AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION. THE ARBITRATOR MAY NOT CONSOLIDATE MORE THAN ONE PERSON'S CLAIMS AND MAY NOT PRESIDE OVER ANY FORM OF CLASS OR REPRESENTATIVE PROCEEDING.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Costs:</strong> Filing fees for arbitration will be allocated according to the applicable AAA Consumer Arbitration Rules. Five Pack Creative, Inc. will pay arbitration costs for claims under $10,000 unless the arbitrator determines the claim is frivolous.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Governing Law:</strong> These terms are governed by the laws of the State of Texas, without regard to conflict of law principles.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Termination
        </Typography>
        <Typography variant="body1" paragraph>
          Either party may terminate this agreement at any time. Upon termination:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Your access to the platform will be revoked</Typography>
          <Typography component="li" variant="body1">Your photos may be retained according to our Privacy Policy and our Acceptable Use Policy</Typography>
          <Typography component="li" variant="body1">Outstanding subscription fees remain due</Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Changes to Terms
        </Typography>
        <Typography variant="body1" paragraph>
          We may update these Terms of Use periodically. Continued use of Snapperish after changes constitutes acceptance of the new terms. Significant changes will be communicated via email or platform notifications.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Contact Information
        </Typography>
        <Typography variant="body1" paragraph>
          Questions about these Terms of Use? Please{' '}
          <Link to="/contact" style={{ color: 'inherit', textDecoration: 'underline' }}>
            contact us
          </Link>{' '}
          or reach Five Pack Creative, Inc. directly at{' '}
          <a href="mailto:matt+snapperish+privacy@fivepack.com" style={{ color: 'inherit' }}>matt+snapperish+privacy@fivepack.com</a>.
          Our mailing address is: Five Pack Creative, Inc., 7460 Warren Parkway, Suite 100, Frisco, TX 75034.
        </Typography>

        <Box sx={{
          textAlign: 'center',
          marginTop: '40px',
          padding: '20px',
          backgroundColor: (theme) => theme.palette.action.hover,
          borderRadius: '8px'
        }}>
          <Typography variant="body2" color="text.secondary">
            Last updated: April 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ marginTop: '8px' }}>
            By using Snapperish, you agree to these terms and our{' '}
            <Link to="/privacy" style={{ color: 'inherit' }}>Privacy Policy</Link> and{' '}
            <Link to="/acceptableuse" style={{ color: 'inherit' }}>Acceptable Use Policy</Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Terms;